import React, { useEffect, useState, useRef  } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import './PrincipalStats.css'

const PrincipalStats = ({ data }) => {
    const { t, i18n } = useTranslation(); // Hook para traducciones
    const scrollRef = useRef(null);

    // Funciones para mover el scroll
    const scrollLeft = () => {
        scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    };

    const scrollRight = () => {
        scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    };

    const { valoracion, numreseñas, allReviews, productivityAverage } = data;

    // Encuentra la última reseña
    const lastReview = allReviews ? allReviews.reduce((latest, review) => {
        return review.timestamp > latest.timestamp ? review : latest;
    }, allReviews[0]) : {};

    const ratingCounts = allReviews ? allReviews.reduce((counts, review) => {
        const rating = review.rating;
        if (rating !== null) {
            counts[rating] = (counts[rating] || 0) + 1;
        }
        return counts;
    }, {}) : {};

    const maxRating = allReviews ? Object.keys(ratingCounts).reduce((a, b) => ratingCounts[a] > ratingCounts[b] ? a : b) : 0;

    const formattedDate = new Date(lastReview.date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });

    // Función para contar reseñas por mes
    const countReviewsByMonth = (reviews) => {
        const monthCounts = {};
        reviews.forEach(review => {
            const month = new Date(review.date).getMonth();
            monthCounts[month] = (monthCounts[month] || 0) + 1;
        });
        return monthCounts;
    };

    // Función para contar reseñas por mes y año
    const countReviewsByMonthAndYear = (reviews) => {
        const monthYearCounts = {};
        reviews.forEach(review => {
            const date = new Date(review.date);
            if (isNaN(date.getTime())) {
                console.error('Fecha inválida:', review.date);
                return; // Salta esta revisión si la fecha es inválida
            }
            const monthYear = `${date.getMonth()}-${date.getFullYear()}`;
            monthYearCounts[monthYear] = (monthYearCounts[monthYear] || 0) + 1;
        });
        return monthYearCounts;
    };

    // Función para encontrar el mes y año con más y menos reseñas
    const findMonthYearWithMostAndLeastReviews = (monthYearCounts) => {
        let maxMonthYear = '';
        let minMonthYear = '';
        let maxCount = 0;
        let minCount = Infinity;

        for (const [monthYear, count] of Object.entries(monthYearCounts)) {
            if (count > maxCount) {
                maxCount = count;
                maxMonthYear = monthYear;
            }
            if (count < minCount) {
                minCount = count;
                minMonthYear = monthYear;
            }
        }

        const currentYear = new Date().getFullYear();
        const defaultMonth = 0;

        const parseMonthYear = (monthYear) => {
            if (!monthYear) return { month: defaultMonth, year: currentYear };
            const [month, year] = monthYear.split('-');
            return { month: parseInt(month), year: parseInt(year) };
        };

        return {
            max: parseMonthYear(maxMonthYear),
            min: parseMonthYear(minMonthYear)
        };
    };

    const monthYearCounts = countReviewsByMonthAndYear(allReviews ? allReviews : []);
    const { max, min } = findMonthYearWithMostAndLeastReviews(monthYearCounts);

    const monthNames = [t('months.january'), t('months.february'), t('months.march'), t('months.april'), t('months.may'), t('months.june'), t('months.july'), t('months.august'), t('months.september'), t('months.october'), t('months.november'), t('months.december')];
    const maxMonthName = `${monthNames[max.month]} / ${max.year}`;
    const minMonthName = `${monthNames[min.month]} / ${min.year}`;

    useEffect(() => {
        const checkScrollPosition = () => {
            const scrollElement = scrollRef.current;
            const screenWidth = window.innerWidth;
    
            if (scrollElement && screenWidth <= 1455) {
                // Lógica para mostrar/ocultar botones según el scroll
                if (scrollElement.scrollLeft <= 0) {
                    document.querySelector('.arrow-button.left').style.display = 'none';
                } else {
                    document.querySelector('.arrow-button.left').style.display = 'block';
                }
    
                if (scrollElement.scrollWidth - scrollElement.scrollLeft === scrollElement.clientWidth) {
                    document.querySelector('.arrow-button.right').style.display = 'none';
                } else {
                    document.querySelector('.arrow-button.right').style.display = 'block';
                }
            } else if (scrollElement) {
                // Asegurarse de que los botones estén ocultos en pantallas grandes
                document.querySelector('.arrow-button.left').style.display = 'none';
                document.querySelector('.arrow-button.right').style.display = 'none';
            }
        };
    
        const handleResize = () => {
            const scrollElement = scrollRef.current;
            if (scrollElement) {
                // Resetea el scroll al redimensionar la ventana
                scrollElement.scrollLeft = 0; 
            }
            checkScrollPosition(); // Recalcular posición del scroll y visibilidad de botones
        };
    
        window.addEventListener('resize', handleResize); // Agregar listener de redimensionamiento
        scrollRef.current.addEventListener('scroll', checkScrollPosition);
        checkScrollPosition(); // Comprobar inicialmente
    
        return () => {
            window.removeEventListener('resize', handleResize); // Limpiar listener al desmontar
            if (scrollRef.current) {
                scrollRef.current.removeEventListener('scroll', checkScrollPosition);
            }
        };
    }, []);

    return (
        <div className='principal-stats-container'>
            {/* Botón para desplazarse a la izquierda */}
            <button className="arrow-button left" onClick={scrollLeft}>
                <i className="ph-caret-circle-left"/>
            </button>

            <div className="principal-stats row" ref={scrollRef}>
                <div className="card-wrapper">
                    <div className="card text-center">
                        <div className="card-body p-2">
                            <div className="svg-center" id="hours-available-progress">
                                <div className="position-relative">
                                    <svg width="60" height="60">
                                        <g transform="translate(30,30)">
                                            <path className="d3-progress-background" d="M0,30A30,30 0 1,1 0,-30A30,30 0 1,1 0,30M0,28A28,28 0 1,0 0,-28A28,28 0 1,0 0,28Z" style={{ fill: '#D6453D', opacity: '1' }} />
                                            {/* <path className="d3-progress-foreground" filter="url(#blur)" d="M1.83697019872103e-15,-30A30,30 0 1,1 -27.015443788569065,12.764414107374622L-25.73650581956699,12.148669079179866A28,28 0 1,0 1.83697019872103e-15,-28Z" style={{ fill: '#D6453D', stroke: '#D6453D' }} /> */}
                                            <path className="d3-progress-front" d="M1.83697019872103e-15,-30A30,30 0 1,1 -27.015443788569065,12.764414107374622L-25.73650581956699,12.148669079179866A28,28 0 1,0 1.83697019872103e-15,-28Z" style={{ fill: '#D6453D', fillOpacity: '1' }} />
                                        </g>
                                    </svg>
                                    <i className="ph-chat-circle counter-icon" style={{color:'#D6453D'}}></i>
                                </div>
                                <h5 className="pt-1 mt-1 mb-0">{numreseñas}</h5>
                                <div>{t('totalReviews')}</div>
                                <div id="hours-available-bars">
                                    <svg width="358.3999938964844" height="40">
                                        <g width="358.3999938964844">
                                            <rect className="d3-random-bars" width="10.324279659569507" x="4.424691282672646" height="20" y="20" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="19.1736622249148" height="20" y="20" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="33.92263316715695" height="24" y="16" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="48.67160410939911" height="28" y="12" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="63.420575051641265" height="22" y="18" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="78.16954599388342" height="34" y="6" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="92.91851693612557" height="30" y="10" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="107.66748787836772" height="36" y="4" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="122.41645882060988" height="40" y="0" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="137.16542976285206" height="34" y="6" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="151.9144007050942" height="22" y="18" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="166.66337164733636" height="34" y="6" style={{ fill: '#D6453D' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="181.41234258957851" height="34" y="6" style={{ fill: '#D6453D' }}></rect>
                                        </g>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-wrapper">
                    <div className="card text-center">
                        <div className="card-body p-2">
                            <div className="svg-center" id="goal-progress">
                                <div className="position-relative">
                                    <svg width="60" height="60">
                                        <g transform="translate(30,30)">
                                            <path className="d3-progress-background" d="M0,30A30,30 0 1,1 0,-30A30,30 0 1,1 0,30M0,28A28,28 0 1,0 0,-28A28,28 0 1,0 0,28Z" style={{ fill: '#273C5A', opacity: '1' }} />
                                            {/* <path className="d3-progress-foreground" filter="url(#blur)" d="M1.83697019872103e-15,-30A30,30 0 1,1 -27.01544378856908,-12.764414107374588L-25.736505819567006,-12.148669079179822A28,28 0 1,0 1.83697019872103e-15,-28Z" style={{ fill: '#273C5A', stroke: '#273C5A' }} /> */}
                                            <path className="d3-progress-front" d="M1.83697019872103e-15,-30A30,30 0 1,1 -27.01544378856908,-12.764414107374588L-25.736505819567006,-12.148669079179822A28,28 0 1,0 1.83697019872103e-15,-28Z" style={{ fill: '#273C5A', fillOpacity: '1' }} />
                                        </g>
                                    </svg>
                                    <i className="ph-trophy counter-icon" style={{color:'#273C5A'}}></i>
                                </div>
                                <h5 className="pt-1 mt-1 mb-0">{valoracion}</h5>
                                <div>{t('averageRating')}</div>
                                <div id="hours-available-bars">
                                    <svg width="358.3999938964844" height="40">
                                        <g width="358.3999938964844">
                                            <rect className="d3-random-bars" width="10.324279659569507" x="92.91851693612557" height="30" y="10" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="137.16542976285206" height="34" y="6" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="151.9144007050942" height="22" y="18" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="33.92263316715695" height="24" y="16" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="48.67160410939911" height="28" y="12" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="19.1736622249148" height="20" y="20" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="63.420575051641265" height="22" y="18" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="78.16954599388342" height="34" y="6" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="107.66748787836772" height="36" y="4" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="122.41645882060988" height="40" y="0" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="166.66337164733636" height="34" y="6" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="4.424691282672646" height="20" y="20" style={{ fill: '#273C5A' }}></rect>
                                            <rect className="d3-random-bars" width="10.324279659569507" x="181.41234258957851" height="34" y="6" style={{ fill: '#273C5A' }}></rect>
                                        </g>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-wrapper">
                    <div className="card text-center">
                        <div className="card-body p-2">
                            <div className="svg-center" id="goal-progress">
                                <div className="position-relative">
                                    <svg width="60" height="60">
                                        <g transform="translate(30,30)">
                                            <path
                                                className="d3-progress-background"
                                                d="M0,30A30,30 0 1,1 0,-30A30,30 0 1,1 0,30M0,28A28,28 0 1,0 0,-28A28,28 0 1,0 0,28Z"
                                                style={{ fill: '#EFC94C', opacity: '1' }}
                                            />
                                            {/* <path
                                            className="d3-progress-foreground"
                                            filter="url(#blur)"
                                            d="M1.83697019872103e-15,-30A30,30 0 1,1 -27.01544378856908,-12.764414107374588L-25.736505819567006,-12.148669079179822A28,28 0 1,0 1.83697019872103e-15,-28Z"
                                            style={{ fill: '#EFC94C', stroke: '#EFC94C' }}
                                        /> */}
                                            <path
                                                className="d3-progress-front"
                                                d="M1.83697019872103e-15,-30A30,30 0 1,1 -27.01544378856908,-12.764414107374588L-25.736505819567006,-12.148669079179822A28,28 0 1,0 1.83697019872103e-15,-28Z"
                                                style={{ fill: '#EFC94C', fillOpacity: '1' }}
                                            />
                                        </g>
                                    </svg>
                                    <i className="ph-star counter-icon" style={{color:'#EFC94C'}}></i>
                                </div>
                                <h5 className="pt-1 mt-1 mb-0">{maxRating} {t('stars')}</h5>
                                <div>{t('maxRating')}</div>
                                <svg width="358.3999938964844" height="40">
                                    <g width="358.3999938964844">
                                        <rect className="d3-random-bars" width="10.324279659569507" x="92.91851693612557" height="30" y="10" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="137.16542976285206" height="34" y="6" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="151.9144007050942" height="22" y="18" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="33.92263316715695" height="24" y="16" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="48.67160410939911" height="28" y="12" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="19.1736622249148" height="20" y="20" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="63.420575051641265" height="22" y="18" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="78.16954599388342" height="34" y="6" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="107.66748787836772" height="36" y="4" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="122.41645882060988" height="40" y="0" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="166.66337164733636" height="34" y="6" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="4.424691282672646" height="20" y="20" style={{ fill: '#EFC94C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="181.41234258957851" height="34" y="6" style={{ fill: '#EFC94C' }}></rect>
                                    </g>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-wrapper">
                    <div className="card text-center">
                        <div className="card-body p-2">
                            <div className="svg-center" id="goal-progress">
                                <div className="position-relative">
                                    <svg width="60" height="60">
                                        <g transform="translate(30,30)">
                                            <path
                                                className="d3-progress-background"
                                                d="M0,30A30,30 0 1,1 0,-30A30,30 0 1,1 0,30M0,28A28,28 0 1,0 0,-28A28,28 0 1,0 0,28Z"
                                                style={{ fill: '#A8DFF7', opacity: '1' }}
                                            />
                                            <path
                                                className="d3-progress-front"
                                                d="M1.83697019872103e-15,-30A30,30 0 1,1 -27.01544378856908,-12.764414107374588L-25.736505819567006,-12.148669079179822A28,28 0 1,0 1.83697019872103e-15,-28Z"
                                                style={{ fill: '#A8DFF7', fillOpacity: '1' }}
                                            />
                                        </g>
                                    </svg>
                                    <i className="ph-calendar counter-icon" style={{color:'#A8DFF7'}}></i>
                                </div>
                                <h5 className="pt-1 mt-1 mb-0">{formattedDate}</h5>
                                <div>{t('lastReview')}</div>
                                <svg width="358.3999938964844" height="40">
                                    <g width="358.3999938964844">
                                        <rect className="d3-random-bars" width="10.324279659569507" x="92.91851693612557" height="30" y="10" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="137.16542976285206" height="34" y="6" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="151.9144007050942" height="22" y="18" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="33.92263316715695" height="24" y="16" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="48.67160410939911" height="28" y="12" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="19.1736622249148" height="20" y="20" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="63.420575051641265" height="22" y="18" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="78.16954599388342" height="34" y="6" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="107.66748787836772" height="36" y="4" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="122.41645882060988" height="40" y="0" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="166.66337164733636" height="34" y="6" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="4.424691282672646" height="20" y="20" style={{ fill: '#A8DFF7' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="181.41234258957851" height="34" y="6" style={{ fill: '#A8DFF7' }}></rect>
                                    </g>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-wrapper">
                    <div className="card text-center">
                        <div className="card-body p-2">
                            <div className="svg-center" id="goal-progress">
                                <div className="position-relative">
                                    <svg width="60" height="60">
                                        <g transform="translate(30,30)">
                                            <path
                                                className="d3-progress-background"
                                                d="M0,30A30,30 0 1,1 0,-30A30,30 0 1,1 0,30M0,28A28,28 0 1,0 0,-28A28,28 0 1,0 0,28Z"
                                                style={{ fill: '#6BA368', opacity: '1' }}
                                            />
                                            {/* <path
                                            className="d3-progress-foreground"
                                            filter="url(#blur)"
                                            d="M1.83697019872103e-15,-30A30,30 0 1,1 -27.01544378856908,-12.764414107374588L-25.736505819567006,-12.148669079179822A28,28 0 1,0 1.83697019872103e-15,-28Z"
                                            style={{ fill: '#6BA368', stroke: '#6BA368' }}
                                        /> */}
                                            <path
                                                className="d3-progress-front"
                                                d="M1.83697019872103e-15,-30A30,30 0 1,1 -27.01544378856908,-12.764414107374588L-25.736505819567006,-12.148669079179822A28,28 0 1,0 1.83697019872103e-15,-28Z"
                                                style={{ fill: '#6BA368', fillOpacity: '1' }}
                                            />
                                        </g>
                                    </svg>
                                    <i className="ph-arrow-elbow-right-up counter-icon" style={{color:'#6BA368'}}></i>
                                </div>
                                <h5 className="pt-1 mt-1 mb-0">{maxMonthName}</h5>
                                <div>{t('bestMonth')}</div>
                                <svg width="358.3999938964844" height="40">
                                    <g width="358.3999938964844">
                                        <rect className="d3-random-bars" width="10.324279659569507" x="92.91851693612557" height="30" y="10" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="137.16542976285206" height="34" y="6" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="151.9144007050942" height="22" y="18" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="33.92263316715695" height="24" y="16" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="48.67160410939911" height="28" y="12" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="19.1736622249148" height="20" y="20" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="63.420575051641265" height="22" y="18" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="78.16954599388342" height="34" y="6" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="107.66748787836772" height="36" y="4" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="122.41645882060988" height="40" y="0" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="166.66337164733636" height="34" y="6" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="4.424691282672646" height="20" y="20" style={{ fill: '#6BA368' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="181.41234258957851" height="34" y="6" style={{ fill: '#6BA368' }}></rect>
                                    </g>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-wrapper">
                    <div className="card text-center">
                        <div className="card-body p-2">
                            <div className="svg-center" id="goal-progress">
                                <div className="position-relative">
                                    <svg width="60" height="60">
                                        <g transform="translate(30,30)">
                                            <path
                                                className="d3-progress-background"
                                                d="M0,30A30,30 0 1,1 0,-30A30,30 0 1,1 0,30M0,28A28,28 0 1,0 0,-28A28,28 0 1,0 0,28Z"
                                                style={{ fill: '#6D4E9C', opacity: '1' }}
                                            />
                                            <path
                                                className="d3-progress-front"
                                                d="M1.83697019872103e-15,-30A30,30 0 1,1 -27.01544378856908,-12.764414107374588L-25.736505819567006,-12.148669079179822A28,28 0 1,0 1.83697019872103e-15,-28Z"
                                                style={{ fill: '#6D4E9C', fillOpacity: '1' }}
                                            />
                                        </g>
                                    </svg>
                                    <i className="ph-arrow-elbow-right-down counter-icon" style={{color:'#6D4E9C'}}></i>
                                </div>
                                <h5 className="pt-1 mt-1 mb-0">{minMonthName}</h5>
                                <div>{t('worstMonth')}</div>
                                <svg width="358.3999938964844" height="40">
                                    <g width="358.3999938964844">
                                        <rect className="d3-random-bars" width="10.324279659569507" x="92.91851693612557" height="30" y="10" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="137.16542976285206" height="34" y="6" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="151.9144007050942" height="22" y="18" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="33.92263316715695" height="24" y="16" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="48.67160410939911" height="28" y="12" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="19.1736622249148" height="20" y="20" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="63.420575051641265" height="22" y="18" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="78.16954599388342" height="34" y="6" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="107.66748787836772" height="36" y="4" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="122.41645882060988" height="40" y="0" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="166.66337164733636" height="34" y="6" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="4.424691282672646" height="20" y="20" style={{ fill: '#6D4E9C' }}></rect>
                                        <rect className="d3-random-bars" width="10.324279659569507" x="181.41234258957851" height="34" y="6" style={{ fill: '#6D4E9C' }}></rect>
                                    </g>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Botón para desplazarse a la derecha */}
            <button className="arrow-button right" onClick={scrollRight}>
                <i className="ph-caret-circle-right"/>
            </button>
        </div>
    );
};

export default PrincipalStats;
